import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { SaraLogo } from '@/components/SaraLogo';
import { GoogleMetricsOverlay } from '@/components/GoogleMetricsOverlay';
import { Mail, Loader2 } from 'lucide-react';

export const HeroSection = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const formData = new FormData(e.target);

    try {
      const response = await fetch('https://formspree.io/f/meeladad', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        navigate('/thank-you');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen bg-hero overflow-hidden">
      <GoogleMetricsOverlay />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        {/* Logo */}
        <div className="mb-10 lg:mb-16">
          <SaraLogo variant="dark" size="large" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left - Headline */}
          <div className="pt-0 lg:pt-4">
            <h1 data-testid="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[hsl(220_15%_95%)] leading-tight mb-6">
              Fully Managed, Done For You{' '}
              <span className="text-primary">Google Ads</span>
            </h1>
            <p className="text-[hsl(222_15%_55%)] text-base lg:text-lg leading-relaxed max-w-lg">
              Freelance Google Ads consultant delivering premium PPC strategy,
              buildout, and ongoing management for businesses that want real,
              measurable growth. I strive to overdeliver.
            </p>
          </div>

          {/* Right - Form */}
          <div className="bg-[hsl(222_30%_14%_/_0.6)] backdrop-blur-sm border border-[hsl(222_20%_22%)] rounded-xl p-6 lg:p-8">
            <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5">
              <input type="hidden" name="_subject" value="New Lead from Sara Advertising PPC" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-primary text-sm font-semibold mb-2 block">Name</Label>
                  <Input
                    data-testid="input-name"
                    name="name"
                    placeholder="Name"
                    required
                    className="bg-card text-card-foreground border-0 h-11 placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <Label className="text-primary text-sm font-semibold mb-2 block">Company</Label>
                  <Input
                    data-testid="input-company"
                    name="company"
                    placeholder="Company"
                    className="bg-card text-card-foreground border-0 h-11 placeholder:text-muted-foreground"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-primary text-sm font-semibold mb-2 block">Phone</Label>
                  <Input
                    data-testid="input-phone"
                    name="phone"
                    placeholder="Phone"
                    type="tel"
                    required
                    className="bg-card text-card-foreground border-0 h-11 placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <Label className="text-primary text-sm font-semibold mb-2 block">Email</Label>
                  <Input
                    data-testid="input-email"
                    name="email"
                    placeholder="Email"
                    type="email"
                    required
                    className="bg-card text-card-foreground border-0 h-11 placeholder:text-muted-foreground"
                  />
                </div>
              </div>
              <div>
                <Label className="text-primary text-sm font-semibold mb-2 block">Message</Label>
                <Textarea
                  data-testid="input-message"
                  name="message"
                  placeholder="How can I help?"
                  rows={4}
                  className="bg-card text-card-foreground border-0 resize-none placeholder:text-muted-foreground"
                />
              </div>
              {error && <p data-testid="form-error" className="text-destructive text-sm">{error}</p>}
              <Button
                data-testid="submit-btn"
                type="submit"
                size="lg"
                className="w-full h-12 text-base font-semibold shadow-[var(--shadow-primary-glow)] hover:shadow-[0_6px_28px_-4px_hsl(185_72%_38%_/_0.5)]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Send Message</>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
